import * as fs from 'fs';
import * as path from 'path';
import { StandardsManager } from '../core/standardsManager';
import { ConsoleLogger } from '../core/types';
import { SmartAgentMatcher } from '../core/smartAgentMatcher';

/**
 * 紧凑规范工具 - Token 优化版 + 项目智能分析
 * 
 * v2.9.0 新特性：
 * - 支持通过 projectPath 自动分析项目技术栈
 * - 如果存在 copilot-instructions.md，优先使用用户配置的 Agents
 * - 智能补充用户未配置的规范
 */
export async function getCompactStandards(args: {
    currentFile?: string;
    fileContent?: string;
    scenario?: string;
    projectPath?: string; // 新增：项目路径，用于智能分析
    mode?: 'summary' | 'key-rules' | 'full';
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    const manager = new StandardsManager();
    const mode = normalizeMode(args.mode);
    
    try {
        // 1. 检测上下文
        let context = detectContext(args, logger);
        
        // 2. 如果提供了 projectPath，进行项目级智能分析
        if (args.projectPath && fs.existsSync(args.projectPath)) {
            const projectContext = await analyzeProject(args.projectPath, logger);
            // 合并项目上下文和文件上下文
            context = mergeContexts(context, projectContext);
        }
        
        // 获取相关规范 URI
        const standardUris = manager.getRelevantStandards({
            fileType: context.fileType,
            imports: context.imports,
            scenario: args.scenario || context.scenario
        });

        // 根据模式返回不同内容
        const responseContent: {
            success?: boolean;
            mode?: string;
            message?: string;
            standards?: Array<{ uri: string; name: string; description?: string }>;
            stats?: { count?: number; estimatedTokens?: number; rulesCount?: number; standardsCount?: number; contentLength?: number };
            context?: { fileType: string; detectedImports: string[]; scenario: string };
            keyRules?: string;
            standardUris?: string[];
            content?: string;
            projectInfo?: { hasUserConfig: boolean; userAgents: string[]; message: string };
            error?: string;
        } = {};
        
        switch (mode) {
            case 'summary':
                Object.assign(responseContent, buildSummaryResponse(standardUris, manager));
                break;
                
            case 'key-rules':
                Object.assign(responseContent, buildKeyRulesResponse(standardUris, context, manager));
                break;
                
            case 'full':
                Object.assign(responseContent, buildFullResponse(standardUris, manager));
                break;
        }
        
        // 添加项目配置信息
        if (args.projectPath && context.userConfiguredAgents) {
            responseContent.projectInfo = {
                hasUserConfig: true,
                userAgents: context.userConfiguredAgents,
                message: '✅ 已读取用户配置的 Agents，并补充了缺失的规范'
            };
        }

        return {
            content: [{
                type: 'text',
                text: JSON.stringify(responseContent, null, 2)
            }]
        };
    } catch (error) {
        logger.error(`规范获取失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}

function normalizeMode(mode?: string): 'summary' | 'key-rules' | 'full' {
    if (mode === 'summary' || mode === 'key-rules' || mode === 'full') {
        return mode;
    }
    return 'key-rules';
}

/**
 * 分析项目并读取用户配置
 */
async function analyzeProject(projectPath: string, logger: ConsoleLogger): Promise<{
    fileType: string;
    imports: string[];
    scenario: string;
    userConfiguredAgents?: string[];
}> {
    const result: {
        fileType: string;
        imports: string[];
        scenario: string;
        userConfiguredAgents?: string[];
    } = {
        fileType: 'unknown',
        imports: [],
        scenario: ''
    };
    
    // 1. 读取用户的 copilot-instructions.md（如果存在）
    const instructionsPath = path.join(projectPath, '.github', 'copilot-instructions.md');
    if (fs.existsSync(instructionsPath)) {
        try {
            const content = fs.readFileSync(instructionsPath, 'utf-8');
            const agents = extractUserAgents(content);
            if (agents.length > 0) {
                result.userConfiguredAgents = agents;
                logger.log(`✅ 检测到用户配置的 Agents: ${agents.join(', ')}`);
            }
        } catch (error) {
            logger.error(`读取用户配置失败: ${error}`);
        }
    }
    
    // 2. 分析项目技术栈
    const matcher = new SmartAgentMatcher(logger);
    const workspaceFolder = {
        uri: { fsPath: projectPath },
        name: path.basename(projectPath),
        index: 0
    };
    
    try {
        const features = await matcher.analyzeProject(workspaceFolder);
        
        // 提取技术栈信息
        if (features.frameworks?.includes('vue')) {
            result.fileType = 'vue';
            result.imports.push('vue');
        }
        if (features.frameworks?.includes('flutter')) {
            result.fileType = 'dart';
            result.imports.push('flutter');
        }
        
        if (features.libraries) {
            result.imports.push(...features.libraries);
        }
        
        logger.log(`🔍 项目技术栈: ${result.imports.join(', ')}`);
    } catch (error) {
        logger.error(`项目分析失败: ${error}`);
    }
    
    return result;
}

/**
 * 从 copilot-instructions.md 中提取用户配置的 Agent IDs
 */
function extractUserAgents(content: string): string[] {
    const agents: string[] = [];
    
    // 匹配 Agent ID 行：- **Agent ID**: `vue3`
    const agentIdRegex = /- \*\*Agent ID\*\*:\s*`([^`]+)`/g;
    let match;
    
    while ((match = agentIdRegex.exec(content)) !== null) {
        agents.push(match[1]);
    }
    
    return agents;
}

/**
 * 合并文件上下文和项目上下文
 */
function mergeContexts(
    fileContext: { fileType: string; imports: string[]; scenario: string },
    projectContext: { fileType: string; imports: string[]; scenario: string; userConfiguredAgents?: string[] }
): { fileType: string; imports: string[]; scenario: string; userConfiguredAgents?: string[] } {
    return {
        fileType: fileContext.fileType !== 'unknown' ? fileContext.fileType : projectContext.fileType,
        imports: [...new Set([...fileContext.imports, ...projectContext.imports])],
        scenario: fileContext.scenario || projectContext.scenario,
        userConfiguredAgents: projectContext.userConfiguredAgents
    };
}

/**
 * 检测上下文
 */
function detectContext(args: {
    currentFile?: string;
    fileContent?: string;
    scenario?: string;
    projectPath?: string;
    mode?: 'summary' | 'key-rules' | 'full';
}, logger: ConsoleLogger): {
    fileType: string;
    imports: string[];
    scenario: string;
} {
    let fileType = 'unknown';
    let imports: string[] = [];
    let scenario = args.scenario || '';

    // 从文件路径检测
    if (args.currentFile && fs.existsSync(args.currentFile)) {
        const ext = args.currentFile.split('.').pop()?.toLowerCase() || '';
        const extMap: Record<string, string> = {
            'vue': 'vue', 'ts': 'ts', 'tsx': 'tsx', 
            'js': 'js', 'jsx': 'jsx', 'dart': 'dart'
        };
        fileType = extMap[ext] || 'unknown';

        try {
            const content = fs.readFileSync(args.currentFile, 'utf-8');
            imports = extractImports(content);
            scenario = inferScenario(content, fileType);
        } catch {
            // 忽略读取错误
        }
    }

    // 从内容检测
    if (args.fileContent) {
        imports = [...imports, ...extractImports(args.fileContent)];
        if (fileType === 'unknown') {
            if (args.fileContent.includes('<template>')) fileType = 'vue';
            else if (args.fileContent.includes('interface ')) fileType = 'ts';
            else if (
                args.fileContent.includes("package:flutter/") ||
                args.fileContent.includes('StatelessWidget') ||
                args.fileContent.includes('StatefulWidget') ||
                args.fileContent.includes('Widget build(')
            ) fileType = 'dart';
        }
        scenario = scenario || inferScenario(args.fileContent, fileType);
    }

    return { fileType, imports: [...new Set(imports)], scenario };
}

/**
 * 构建摘要响应（约 500 tokens）
 */
function buildSummaryResponse(uris: string[], manager: StandardsManager): {
    success: boolean;
    mode: string;
    message: string;
    standards: Array<{ uri: string; name: string; description: string }>;
    stats: { count: number; estimatedTokens: number };
} {
    const standards = manager.getAvailableStandards();
    const matched = uris.map(uri => {
        const std = standards.find(s => s.uri === uri);
        return {
            uri,
            name: std?.name || uri,
            description: std?.description || ''
        };
    });

    return {
        success: true,
        mode: 'summary',
        message: '已识别相关规范，如需详细内容请使用 mode: "key-rules" 或 "full"',
        standards: matched,
        stats: {
            count: matched.length,
            estimatedTokens: 500
        }
    };
}

/**
 * 构建关键规则响应（约 2000-3000 tokens）
 * 针对不同场景提取关键规则
 */
function buildKeyRulesResponse(
    uris: string[], 
    context: { fileType: string; imports: string[]; scenario: string },
    manager: StandardsManager
): {
    success: boolean;
    mode: string;
    context: { fileType: string; detectedImports: string[]; scenario: string };
    keyRules: string;
    standardUris: string[];
    stats: { rulesCount: number; estimatedTokens: number };
} {
    const keyRules: string[] = [];
    
    // 根据文件类型添加关键规则
    if (context.fileType === 'vue') {
        keyRules.push(...getVueKeyRules(context.imports));
        keyRules.push(...getCssKeyRules()); // Vue 组件默认包含 CSS 规范
    } else if (context.fileType === 'dart') {
        keyRules.push(...getFlutterKeyRules());
        if (isDesignRestorationScenario(context.scenario, context.imports)) {
            keyRules.push(...getFlutterDesignRestorationKeyRules());
        }
    } else if (context.fileType === 'ts' || context.fileType === 'tsx') {
        keyRules.push(...getTypeScriptKeyRules());
    }
    
    // 根据导入添加特定规则
    if (context.imports.includes('element-plus')) {
        keyRules.push(...getElementPlusKeyRules());
    }
    if (context.imports.some(i => i.includes('i18n') || i.includes('locale'))) {
        keyRules.push(...getI18nKeyRules());
    }
    if (context.imports.includes('pinia')) {
        keyRules.push(...getPiniaKeyRules());
    }
    
    // 根据场景添加规则
    if (context.scenario.includes('样式') || context.scenario.includes('CSS')) {
        if (!keyRules.some(r => r.includes('CSS 样式规范'))) {
            keyRules.push(...getCssKeyRules());
        }
    }

    return {
        success: true,
        mode: 'key-rules',
        context: {
            fileType: context.fileType,
            detectedImports: context.imports,
            scenario: context.scenario
        },
        keyRules: keyRules.join('\n\n'),
        standardUris: uris, // 提供完整 URI 供按需加载
        stats: {
            rulesCount: keyRules.length,
            estimatedTokens: Math.ceil(keyRules.join('').length / 4)
        }
    };
}

/**
 * 构建完整响应（原有行为）
 */
function buildFullResponse(uris: string[], manager: StandardsManager): {
    success: boolean;
    mode: string;
    standards: string[];
    content: string;
    stats: { standardsCount: number; contentLength: number; estimatedTokens: number };
} {
    const content = manager.combineStandards(uris);
    return {
        success: true,
        mode: 'full',
        standards: uris,
        content,
        stats: {
            standardsCount: uris.length,
            contentLength: content.length,
            estimatedTokens: Math.ceil(content.length / 4)
        }
    };
}

// ==================== 关键规则提取 ====================

function getVueKeyRules(imports: string[]): string[] {
    return [
        `## Vue 3 关键规则

1. **使用 \`<script setup lang="ts">\`** - 必须使用 Composition API
2. **Props/Emits 类型定义** - 必须使用 interface 定义类型
3. **禁止内联样式** - 使用 scoped CSS 或 class
4. **模板简洁** - 复杂逻辑提取到 computed 或方法
5. **响应式** - 基本类型用 ref，对象用 reactive（不重新赋值）`,

        `## 组件结构顺序
\`\`\`vue
<script setup lang="ts">
// 1. 类型导入
// 2. Props/Emits 定义
// 3. 响应式状态
// 4. 计算属性
// 5. 方法
// 6. 生命周期
</script>
\`\`\``
    ];
}

function getElementPlusKeyRules(): string[] {
    return [
        `## Element Plus 关键规则

### 表格规范
- **必须** \`border\` + \`highlight-current-row\`
- 操作列 \`fixed="right"\`，宽度：2按钮160px/3按钮240px/4按钮280px
- 使用 \`v-loading\` 显示加载状态

### 表单规范
- 使用 \`label-position="top"\` 或 \`label-width="120px"\`
- 验证规则使用 \`FormRules<T>\` 类型
- 验证消息必须国际化

### 弹窗规范
- 使用 \`destroy-on-close\` 重置状态
- 宽度：简单500px/标准700px/复杂900px

### 消息提示
\`\`\`typescript
ElMessage.success($t('保存成功'))
ElMessageBox.confirm($t('确认删除？'), $t('警告'), { type: 'warning' })
\`\`\``,

        `## 表格示例
\`\`\`vue
<el-table v-loading="listLoading" :data="list" border highlight-current-row>
  <el-table-column type="index" :label="$t('序号')" width="70" />
  <el-table-column prop="name" :label="$t('名称')" min-width="120" />
  <el-table-column fixed="right" :label="$t('操作')" width="240">
    <template #default="{ row }">
      <el-button link type="primary" @click="handleEdit(row)">{{ $t('编辑') }}</el-button>
      <el-button link type="danger" @click="handleDelete(row)">{{ $t('删除') }}</el-button>
    </template>
  </el-table-column>
</el-table>
\`\`\``
    ];
}

function getI18nKeyRules(): string[] {
    return [
        `## 国际化关键规则

### 必须国际化的内容
- 所有按钮文字：\`{{ $t('保存') }}\`
- 表单标签：\`:label="$t('客户名称')"\`
- 占位符：\`:placeholder="$t('请输入')"\`
- 表格列标题：\`:label="$t('订单编号')"\`
- 弹窗标题：\`:title="$t('新增客户')"\`
- 提示消息：\`ElMessage.success($t('保存成功'))\`
- 验证规则：\`message: $t('请输入用户名')\`

### 禁止
- ❌ \`<el-button>新增</el-button>\` (硬编码)
- ❌ \`label="客户名称"\` (硬编码)
- ❌ \`ElMessage.success('保存成功')\` (硬编码)`
    ];
}

function getPiniaKeyRules(): string[] {
    return [
        `## Pinia 关键规则

### Setup Store 语法（推荐）
\`\`\`typescript
export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const isLoggedIn = computed(() => !!user.value)
  
  async function login(credentials: Credentials) {
    user.value = await authApi.login(credentials)
  }
  
  return { user, isLoggedIn, login }
})
\`\`\`

### 规范要求
- Store ID 使用小写短横线命名
- 使用 Setup Store 语法而非 Options Store
- 异步操作直接用 async/await
- 复杂状态操作使用 $patch`
    ];
}

function getTypeScriptKeyRules(): string[] {
    return [
        `## TypeScript 关键规则

### 类型定义
- 必须定义函数参数和返回类型
- 使用 interface 定义对象类型
- 避免使用 any，使用 unknown 替代

### 命名规范
- 变量/函数：camelCase
- 类/接口/类型：PascalCase
- 常量：UPPER_SNAKE_CASE

### 示例
\`\`\`typescript
interface User {
  id: number
  name: string
  email?: string
}

async function fetchUser(id: number): Promise<User | null> {
  // ...
}
\`\`\``
    ];
}

function getCssKeyRules(): string[] {
    return [
        `## CSS 样式规范

### 基本原则
- **禁止内联样式** - 不使用 \`style="..."\`，用 CSS 类替代
- Vue 组件使用 \`<style scoped>\` 和嵌套 CSS 语法
- 全局样式使用标准 CSS 语法（带 \`:\`、\`;\`、\`{}\`）

### Element Plus 组件样式
- 通用样式必须通过父容器限定作用域（如 \`.el-form-item\`）
- **避免直接选择基础组件**：不直接选择 \`.el-input\`、\`.el-select\` 等，防止影响复合组件内部布局
- **禁止设置 __wrapper 宽度**：\`.el-input__wrapper\`、\`.el-select__wrapper\` 等只能设置高度、内边距、背景色，不能设置宽度（会影响分页器、日期选择器等）

### 可使用的工具类
\`\`\`css
/* 间距 */
.ml_8, .mr_8, .mt_8, .mb_8  /* 8px margin */
.ml_16, .mr_16, .mt_16, .mb_16  /* 16px margin */

/* 宽度 */
.w_100, .w_120, .w_180, .w_200  /* 固定宽度 */
\`\`\`

### 示例
\`\`\`vue
<style scoped>
/* ✅ 正确：通过父容器限定 */
.my-form :deep(.el-form-item) {
  margin-bottom: 16px;
}

/* ❌ 错误：直接设置全局组件 */
.el-input__wrapper {
  width: 200px;  /* 会影响分页器等其他组件 */
}
\`\`\`
`
    ];
}

function getFlutterKeyRules(): string[] {
    return [
        `## Flutter UI 系统关键规则

### Design Token 快捷方式（必须使用）
| 快捷方式 | 用途 | 示例 |
|---------|------|------|
| \`$c\` | 颜色 | \`$c.primary\`, \`$c.text.primary\` |
| \`$s\` | 间距 | \`$s.sm\`, \`$s.md\`, \`$s.px(14)\` |
| \`$t\` | 排版 | \`$t.displayLarge\`, \`$t.bodyMedium\` |
| \`$r\` | 圆角 | \`$r.sm\`, \`$r.md\`, \`$r.full\` |

### Flex 组件库（必须使用）
- \`FlexButton\` 替代 ElevatedButton/TextButton
- \`FlexCard\` 替代 Card/Container
- \`FlexInput\` 替代 TextField
- \`Gap\` 替代 SizedBox（间隔）

### 禁止
- ❌ \`Color(0xFF3B82F6)\` 硬编码颜色
- ❌ \`EdgeInsets.all(16)\` 硬编码间距
- ❌ \`Colors.blue\` Material 颜色常量`
    ];
}

function getFlutterDesignRestorationKeyRules(): string[] {
    return [
        `## Flutter 设计稿还原关键规则

### 先读页面 contract，再生成代码
- 设计稿还原首轮优先读取 \`sketch_measure(cmd: \"compact\")\` 输出中的 \`restorationContract\`
- 先按 \`blockOrder\` 还原页面骨架，再处理颜色、圆角、阴影和 SVG
- \`bodyCopyLedger\` / \`navCopyLedger\` 中的文案默认为设计真值，**禁止**替换成现有路由标题、i18n 文案或业务分组名

### 结构优先于业务语义
- 现有项目组件、页面标题、常见 CTA 习惯只能作为实现参考，**不能覆盖**测量得到的结构
- 如果 \`restorationContract.topPrimaryEntry\` 存在，禁止把顶部入口迁移为底部固定按钮
- 如果 \`restorationContract.bottomNavigation.present=true\`，且 contract 未声明额外 bottom CTA，禁止再添加一层底部固定操作条

### 数据使用优先级
- 首轮代码生成: \`compact.restorationContract\` + \`compact.tree\`
- 局部样式/图标诊断: 再补读 \`measure\` 或 \`style\`
- 只要 contract 与现有业务组件冲突，优先保证设计稿结构一致，再决定是否复用组件`

        ,`### SVG / 资源回归检查
- 如果 \`iconSvgMeta.colorStrategy=preserve\`，或 SVG 自带 \`fill-opacity\` / 渐变 / 多色路径，**禁止**再加 \`ColorFilter\`
- 旧页面的同名箭头/图标资源默认不可信；局部图标偏差优先从当前画板重新导出，不直接复用全局资产
- 16×16 通常是点击槽位而非图形真实尺寸；当 compact/measure 给出 \`containerSize\` 与 \`contentSize\` 时，必须分离占位与渲染
- Sketch 的 \`Image\` 图层必须导出 PNG + \`Image.asset\`，不要伪装成 SVG 用 \`SvgPicture\``
    ];
}

function isDesignRestorationScenario(scenario: string, imports: string[]): boolean {
    const text = `${scenario} ${imports.join(' ')}`.toLowerCase();
    return [
        '设计稿',
        '还原',
        'sketch',
        'figma',
        '像素',
        'measure',
        'restoration',
    ].some((keyword) => text.includes(keyword));
}

// ==================== 辅助函数 ====================

function extractImports(content: string): string[] {
    const imports: string[] = [];
    
    // ES6 imports
    const esImportRegex = /import\s+(?:(?:\{[^}]*\}|\*\s+as\s+\w+|\w+)\s+from\s+)?['"]([^'"]+)['"]/g;
    let match;
    while ((match = esImportRegex.exec(content)) !== null) {
        imports.push(match[1]);
    }
    
    // require
    const requireRegex = /require\(['"]([^'"]+)['"]\)/g;
    while ((match = requireRegex.exec(content)) !== null) {
        imports.push(match[1]);
    }
    
    return imports;
}

function inferScenario(content: string, fileType: string): string {
    const scenarios: string[] = [];
    const lower = content.toLowerCase();
    
    if (lower.includes('el-table') || lower.includes('el-form')) {
        scenarios.push('表格组件', '表单组件');
    }
    if (lower.includes('$t(') || lower.includes('i18n')) {
        scenarios.push('国际化');
    }
    if (lower.includes('interface ') || lower.includes('type ')) {
        scenarios.push('类型定义');
    }
    
    return scenarios.join('、');
}
